/**
 * Notifications & reminders (section `notifications`, plan 6.G).
 * Four rows: booking confirmation (always on, Q11) · email reminders (feature 7) · SMS reminders
 * (feature 7 + isSmsAllowed) · email follow-up (feature 6, "Coming soon" kept, D11).
 * Timing selects / extra reminders are hidden (Q5). All four flags live on the advanced page.
 */

import React from "react";
import { FEATURE_IDS } from "../../../../../constants/featureAccess";

import Icon from "../../ui/Icon";
import { DowngradeNote, Helper, PlanBadge, Switch } from "../../ui/primitives";
import { useBuilder } from "../../state/builderContext";
import { D, E, STEPS } from "../../state/constants";
import SettingsView from "../shared/SettingsView";

const flip = (v) => (v === E ? D : E);

const Row = ({ icon, title, text, meta, badge, on, disabled, label, onChange, subdued, children }) => (
  <div className={`eb-notification-row ${subdued ? "subdued" : ""}`.trim()}>
    <span className="eb-notify-icon">
      <Icon name={icon} size={17} />
    </span>
    <div>
      <strong>
        {title} {badge || null}
      </strong>
      <p>{text}</p>
      {meta ? <small>{meta}</small> : null}
      {children}
    </div>
    <Switch on={on} disabled={disabled} label={label} onChange={onChange} />
  </div>
);

const NotificationsSection = () => {
  const { state, update, hasFeature, redux } = useBuilder();
  const adv = state.advanced;
  const set = (field) => () => update(STEPS.ADVANCED, { [field]: flip(adv[field]) });

  const remindersAllowed = hasFeature(FEATURE_IDS.AUTOMATED_REMINDERS);
  const smsAllowed = remindersAllowed && redux.smsAllowed;
  const followupAllowed = hasFeature(FEATURE_IDS.AUTOMATED_EVENT_NOTIFICATIONS);

  return (
    <SettingsView icon="bell" title="Notifications & reminders" text="Manage confirmations, reminders, and follow-up communication.">
      <div className="eb-settings-group notification-stack" id="notifications-setting">
        <Row
          icon="mail"
          title="Booking confirmation email"
          text="Sent immediately to the host and invitee."
          meta={"Email \u00B7 Immediately after booking"}
          on
          disabled
          label="Booking confirmation"
          onChange={() => {}}
        />

        <Row
          icon="bell"
          title="Email reminders"
          text="Help invitees remember the meeting."
          badge={!remindersAllowed ? <PlanBadge kind="upgrade" /> : null}
          on={adv.email_reminder_status === E}
          disabled={!remindersAllowed}
          label="Email reminders"
          onChange={set("email_reminder_status")}
        >
          {!remindersAllowed && adv.email_reminder_status === E ? <DowngradeNote>Reminders are not in your plan &mdash; turned off the next time this section is saved.</DowngradeNote> : null}
        </Row>

        <Row
          icon="phone"
          title="SMS reminders"
          text="Send a text before the event. SMS credits are required."
          badge={!remindersAllowed ? <PlanBadge kind="upgrade" /> : !smsAllowed ? <PlanBadge kind="afterTrial" /> : null}
          on={adv.sms_reminder_status === E}
          disabled={!smsAllowed}
          label="SMS reminders"
          onChange={set("sms_reminder_status")}
        >
          {remindersAllowed && !smsAllowed ? <Helper>SMS is available after you subscribe.</Helper> : null}
          {!smsAllowed && adv.sms_reminder_status === E ? <DowngradeNote>SMS reminders are not available &mdash; turned off the next time this section is saved.</DowngradeNote> : null}
        </Row>

        <Row
          icon="send"
          subdued
          title="Email follow-up"
          text="Send a message after the event ends."
          badge={!followupAllowed ? <PlanBadge kind="upgrade" /> : <span className="eb-coming-badge">Coming soon</span>}
          on={adv.email_followup_status === E}
          disabled={!followupAllowed}
          label="Email follow-up"
          onChange={set("email_followup_status")}
        >
          {!followupAllowed && adv.email_followup_status === E ? <DowngradeNote>Follow-ups are not in your plan &mdash; turned off the next time this section is saved.</DowngradeNote> : null}
        </Row>
      </div>
    </SettingsView>
  );
};

export default NotificationsSection;
